// Receipt / screenshot scan helpers — see CLAUDE.md's "Receipt scan" design note.
// The backend (POST /expenses/scan) only returns drafts; nothing is saved until the user reviews.
import api from "@/lib/api";

const MAX_EDGE = 2000;      // px — plenty for receipt text; keeps uploads well under the 5 MB API limit
const JPEG_QUALITY = 0.85;

// Downscale + re-encode to JPEG in the browser. Also converts formats the backend can't take
// (e.g. HEIC on Safari, which can decode it) into JPEG. Falls back to the original file if the
// browser can't decode it — the backend then returns a clear "unsupported format" error.
export async function prepareImageForScan(file) {
    try {
        const url = URL.createObjectURL(file);
        const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = reject;
            el.src = url;
        });
        URL.revokeObjectURL(url);
        const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff"; // transparent PNG screenshots -> white, not black
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", JPEG_QUALITY));
        return blob || file;
    } catch {
        return file;
    }
}

// Returns { document_kind, items: [{type,title,amount,category,payment_method,occurred_at,notes,currency}] }.
// Throws an Error whose message is safe to show the user.
export async function scanReceiptImage(file) {
    if (!file || !file.type?.startsWith("image/")) {
        throw new Error("Please choose an image (JPG, PNG or a screenshot).");
    }
    const prepared = await prepareImageForScan(file);
    const formData = new FormData();
    formData.append("image", prepared, "scan.jpg");
    try {
        const { data } = await api.post("/expenses/scan", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
        });
        return data;
    } catch (e) {
        throw new Error(e.response?.data?.detail || "Couldn't read that image. Try again or enter it manually.");
    }
}

// Pulls the first image out of a paste event (Ctrl/Cmd+V of a screenshot), or null.
export function imageFromPaste(e) {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
        if (it.kind === "file" && it.type.startsWith("image/")) return it.getAsFile();
    }
    return null;
}

export const DOC_KIND_LABELS = {
    receipt: "Receipt",
    payment_screenshot: "Payment screenshot",
    order: "Order confirmation",
    statement: "Statement",
    other: "Image",
};
